import { findCountry } from "@/lib/countries";

/**
 * CountryFlag — small pill with the country flag emoji + name.
 * Used on event cards, event detail pages, member directory, etc.
 *
 * Props:
 *   - country: string (the country display name, e.g. "France", "Italie")
 *   - size: "xs" | "sm" | "md" (default "sm")
 *   - showName: boolean (default true). When false, only the flag is rendered.
 *   - className: extra Tailwind classes
 */
export default function CountryFlag({ country, size = "sm", showName = true, className = "" }) {
  if (!country) return null;
  const c = findCountry(country);
  if (!c) {
    // Fall back to raw string so we don't hide data when an unknown country is set.
    return showName ? (
      <span className={`inline-flex items-center gap-1 text-xs text-neutral-600 ${className}`} data-testid="country-flag-fallback">
        <span aria-hidden="true">🏳️</span> {country}
      </span>
    ) : null;
  }
  const sizes = {
    xs: "text-[10px] px-1.5 py-0.5",
    sm: "text-xs px-2 py-0.5",
    md: "text-sm px-2.5 py-1",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full bg-neutral-100 font-medium text-neutral-700 ${sizes[size] || sizes.sm} ${className}`}
      title={c.name}
      data-testid={`country-flag-${c.code.toLowerCase()}`}
    >
      <span aria-hidden="true" className="text-base leading-none">{c.flag}</span>
      {showName && <span>{c.name}</span>}
    </span>
  );
}
